import {
  biomech,
  events,
  type ContactEvent,
  type PoseFrame,
  type Side,
  type SwingMetrics,
} from '@tbc/core';

const PRE_CONTACT_FRAMES = 30; // ~0.5s at 60fps
const POST_CONTACT_FRAMES = 18; // ~0.3s at 60fps

/**
 * Given a complete pose recording and the user's dominant side, compute the
 * wrist-speed series, detect contacts, and emit a `SwingMetrics` per contact.
 */
export function analyzeSession(
  frames: readonly PoseFrame[],
  fps: number,
  side: Side = 'right',
): { contacts: ContactEvent[]; swings: SwingMetrics[] } {
  if (frames.length === 0) return { contacts: [], swings: [] };

  const wristXy = biomech.wristXyTrack(frames, side);
  const wristSpeed = biomech.wristSpeedSeries(wristXy, fps);
  const contacts = events.detectContacts(wristSpeed, { fps });

  const elbowSeries = biomech.elbowAngleSeriesDeg(frames, side);
  const elbowAngVel = biomech.angularVelocityRadPerSec(elbowSeries, fps);

  const swings: SwingMetrics[] = contacts.map((c) => {
    const start = Math.max(0, c.frameIndex - PRE_CONTACT_FRAMES);
    const end = Math.min(frames.length, c.frameIndex + POST_CONTACT_FRAMES);
    const slice = elbowSeries.slice(start, end);

    let peak = 0;
    for (let i = start; i < end; i++) {
      const v = Math.abs(elbowAngVel[i] ?? 0);
      if (v > peak) peak = v;
    }

    return {
      contactFrame: c.frameIndex,
      contactTimeMs: c.timeMs,
      elbowAngleAtContactDeg: elbowSeries[c.frameIndex] ?? Number.NaN,
      peakWristAngularVelocityRadPerSec: peak,
      swingDurationMs: ((end - start) / fps) * 1000,
      elbowAngleSeriesDeg: slice,
    };
  });

  return { contacts, swings };
}
